import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "@/components/ui/button";
import { Printer, Eye } from "lucide-react";
import { EnhancedInvoice } from "@/types/invoice";
import { useRepairShopContext } from "@/context/RepairShopContext";
import { PDFService } from "@/services/PDFService";
import InvoicePDF from "./InvoicePDF";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface InvoicePDFDownloadProps {
  invoice: EnhancedInvoice;
  variant?: "print" | "preview" | "both";
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const InvoicePDFDownload: React.FC<InvoicePDFDownloadProps> = ({
  invoice,
  variant = "both",
  size = "sm",
  className = "",
}) => {
  const { t } = useTranslation();
  const { repairShop } = useRepairShopContext();
  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: PDFService.generateFilename(invoice),
    pageStyle: `
      @page {
        size: A4;
        margin: 0.5in;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          color-adjust: exact;
        }
      }
    `,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        // Small delay to ensure content is ready
        setTimeout(resolve, 100);
      });
    },
    onAfterPrint: () => {
      toast.success(t("invoices.pdfPrintSuccess"));
    },
    onPrintError: (error) => {
      console.error("Print error:", error);
      toast.error(t("invoices.pdfPrintError"));
    },
  });

  if (!repairShop) {
    return null;
  }

  const handlePrintInvoice = () => {
    if (!componentRef.current) {
      console.error("Print component ref is not available");
      toast.error(t("invoices.pdfPrintError"));
      return;
    }

    try {
      handlePrint();
    } catch (error) {
      console.error("Print error:", error);
      // Fallback to native browser print
      handleFallbackPrint();
    }
  };

  const handleFallbackPrint = () => {
    try {
      // Create a new window with the invoice content
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error(t("invoices.pdfPrintError"));
        return;
      }

      const invoiceContent = componentRef.current?.innerHTML || "";

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${PDFService.generateFilename(invoice)}</title>
            <style>
              @page { size: A4; margin: 0.5in; }
              @media print { body { -webkit-print-color-adjust: exact; color-adjust: exact; } }
              body { font-family: system-ui, -apple-system, sans-serif; line-height: 1.5; color: #000; background: #fff; margin: 0; padding: 20px; }
              .bg-blue-600 { background-color: #2563eb !important; }
              .bg-green-500 { background-color: #10b981 !important; }
              .text-white { color: white !important; }
              .rounded-lg { border-radius: 8px; }
              .font-bold { font-weight: bold; }
              .font-semibold { font-weight: 600; }
              table { border-collapse: collapse; width: 100%; }
              th, td { padding: 12px 8px; text-align: left; }
              th { font-weight: 600; }
            </style>
          </head>
          <body>${invoiceContent}</body>
        </html>
      `);

      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        toast.success(t("invoices.pdfPrintSuccess"));
      }, 500);
    } catch (error) {
      console.error("Fallback print error:", error);
      toast.error(t("invoices.pdfPrintError"));
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Hidden PDF component for printing */}
      <div style={{ display: "none" }}>
        <div ref={componentRef} className="print-content">
          <InvoicePDF invoice={invoice} repairShop={repairShop} />
        </div>
      </div>

      {/* Preview Dialog */}
      {(variant === "preview" || variant === "both") && (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size={size}>
              <Eye className="h-4 w-4 mr-2" />
              {t("invoices.previewPDF")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>
                {t("invoices.invoicePreview")} - {invoice.invoiceNumber}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <InvoicePDF invoice={invoice} repairShop={repairShop} />
              <div className="flex justify-end mt-4 pt-4 border-t">
                <Button onClick={handlePrintInvoice}>
                  <Printer className="h-4 w-4 mr-2" />
                  {t("invoices.printPDF")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Print Button */}
      {(variant === "print" || variant === "both") && (
        <Button variant="outline" size={size} onClick={handlePrintInvoice}>
          <Printer className="h-4 w-4 mr-2" />
          {t("invoices.printPDF")}
        </Button>
      )}
    </div>
  );
};

export default InvoicePDFDownload;
